import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.js";
import { AdminApp } from "./admin/AdminApp.js";
import { EnvironmentBanner } from "./components/EnvironmentBanner.js";
import "./styles/theme.css";

/** True when the current hash selects the owner admin route. */
function isAdminRoute(): boolean {
  const hash = window.location.hash.replace(/^#\/?/, "");
  return hash === "admin" || hash.startsWith("admin/");
}

/**
 * Top-level switch between the public site and the owner admin app.
 *
 * A hash route (`#admin`) keeps the admin views separate from the public site
 * while staying compatible with static SPA hosting (no server rewrite needed).
 */
function Root(): JSX.Element {
  const [admin, setAdmin] = useState<boolean>(() => isAdminRoute());

  useEffect(() => {
    const onHashChange = (): void => setAdmin(isAdminRoute());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  return admin ? <AdminApp /> : <App />;
}

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root container #root not found in document");
}

createRoot(container).render(
  <React.StrictMode>
    <EnvironmentBanner label={import.meta.env.VITE_ENV_LABEL} />
    <Root />
  </React.StrictMode>,
);
